import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, CheckCircle2, Circle, Clock, AlertCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI, SECTION_STATUS_META } from '../lib/onboardingAPI';

const icon = (s) => {
  switch (s) {
    case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'in_progress': return <Clock className="w-4 h-4 text-amber-500" />;
    case 'needs_review': return <AlertCircle className="w-4 h-4 text-orange-500" />;
    default: return <Circle className="w-4 h-4 text-muted-foreground/50" />;
  }
};

export default function OnboardingReview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await onboardingAPI.myChecklist();
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
      navigate('/onboarding');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleSubmit = async () => {
    if (!data?.client?.id) return;
    setSubmitting(true);
    try {
      await onboardingAPI.submit(data.client.id);
      toast.success(data.client.onboarding_submitted ? 'Updates re-submitted — thank you!' : 'Onboarding submitted — thank you!');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit yet');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6 max-w-2xl mx-auto"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  if (!data) return null;

  const sections = data.sections.filter((s) => s.applicable && !s.terminal);
  const submitted = data.client?.onboarding_submitted;
  const updatedAfterSubmit = data.client?.updated_after_submit;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Review &amp; Submit</CardTitle>
          <CardDescription>Check your sections below, then submit your onboarding for your accountant to review.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sections.map((s) => {
            const meta = SECTION_STATUS_META[s.status] || SECTION_STATUS_META.not_started;
            return (
              <div key={s.key} className="flex items-center justify-between border-b last:border-0 py-2.5">
                <span className="flex items-center gap-2 text-foreground/90">
                  {icon(s.status)} {s.title}
                  {s.required && <span className="text-xs text-red-500">required</span>}
                </span>
                <span className="flex items-center gap-3">
                  <Badge className={meta.color}>{meta.label}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/onboarding/section/${s.key}`)}>Edit</Button>
                </span>
              </div>
            );
          })}

          {submitted && !updatedAfterSubmit && (
            <div className="mt-4 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
              Your onboarding has been submitted and is now with your accountant. You can still edit any section above — re-submit afterwards so we review the latest version.
            </div>
          )}
          {submitted && updatedAfterSubmit && (
            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              You've made changes since submitting. Re-submit below so your accountant reviews the latest version.
            </div>
          )}
          {!data.identity_ok && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="identity-required-msg">
              Please complete identity verification before submitting.{' '}
              <button type="button" className="underline" onClick={() => navigate('/onboarding/section/identity_verification')}>Verify my identity</button>
            </div>
          )}
          {!submitted && !data.can_submit && data.identity_ok && (
            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              Please complete the required sections before submitting.
            </div>
          )}
          {(!submitted || updatedAfterSubmit) && (
            <div className="flex justify-end pt-2">
              <Button onClick={handleSubmit} disabled={submitting || !data.can_submit} data-testid="onboarding-submit">
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                  : <><Send className="w-4 h-4 mr-2" /> {submitted ? 'Re-submit Updates' : 'Submit Onboarding'}</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
